<script setup lang="ts">
import { computed, ref } from 'vue';
import { type ShiftConflict } from '@/api/shift-conflicts';

type Mode = 'create' | 'update' | 'batch-update';

interface Props {
  conflicts: Array<ShiftConflict>
  mode?: Mode
}

const props = withDefaults(defineProps<Props>(), {
  conflicts: () => [],
  mode: 'create',
});

const isCreateMode = computed(() => props.mode === 'create');
const actionVerb = computed(() => (isCreateMode.value ? 'crear' : 'actualizar'));
const actionVerbCapitalized = computed(() => (isCreateMode.value ? 'Crear' : 'Actualizar'));
const emit = defineEmits<{
  cancel: []
  confirm: []
}>();

const MAX_VISIBLE_CONFLICTS_PER_CAREGIVER = 5;

const show = ref(true);

const totalConflicts = computed(() => props.conflicts.reduce((sum, c) => sum + c.conflictsCount, 0));
const totalScheduleBlocks = computed(() => props.conflicts.reduce((sum, c) => sum + (c.scheduleBlocksCount ?? 0), 0));
const totalOutsideRequirements = computed(() => props.conflicts.reduce(
  (sum, c) => sum + (c.outsideRequirementsCount ?? 0),
  0,
));
const totalOverlaps = computed(() => props.conflicts.reduce(
  (sum, c) => sum + (c.details ?? []).filter(detail => detail.existingShifts?.length).length,
  0,
));
function shiftNoun(count: number) {
  return count > 1 ? 'turnos' : 'turno';
}
const summaryLabel = computed(() => {
  const parts: string[] = [];
  if (totalOverlaps.value > 0) {
    parts.push(`${totalOverlaps.value} ${totalOverlaps.value > 1 ? 'topes' : 'tope'} de horario`);
  }
  if (totalScheduleBlocks.value > 0) {
    parts.push(`${totalScheduleBlocks.value} ${shiftNoun(totalScheduleBlocks.value)} dentro de un bloqueo de horario`);
  }
  if (totalOutsideRequirements.value > 0) {
    parts.push(
      `${totalOutsideRequirements.value} ${shiftNoun(totalOutsideRequirements.value)} ` +
      'fuera de las fechas del requerimiento',
    );
  }

  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} y ${parts.at(-1)}` : parts.join('');
});
const summaryVerb = computed(() => (
  totalOverlaps.value + totalScheduleBlocks.value + totalOutsideRequirements.value > 1 ? 'detectaron' : 'detectó'
));
const summaryNoun = computed(() => {
  if (totalOverlaps.value > 0) return 'topes de horario';
  if (totalOutsideRequirements.value === 0) return 'turnos dentro de bloqueos de horario';
  if (totalScheduleBlocks.value === 0) return 'turnos fuera de las fechas del requerimiento';

  return 'avisos';
});
const title = computed(() => (totalOverlaps.value > 0 ? 'Topes de Horarios Detectados' : 'Avisos antes de guardar'));

const conflictsWithLimitedDetails = computed(() => props.conflicts.map(conflict => ({
  ...conflict,
  details: conflict.details?.slice(0, MAX_VISIBLE_CONFLICTS_PER_CAREGIVER),
  hasMoreConflicts: (conflict.details?.length || 0) > MAX_VISIBLE_CONFLICTS_PER_CAREGIVER,
  hiddenConflictsCount: Math.max(0, (conflict.details?.length || 0) - MAX_VISIBLE_CONFLICTS_PER_CAREGIVER),
})));

function formatShiftTime(date: string | null | undefined, humanTime: string | null | undefined) {
  if (!date || !humanTime) return 'Fecha no disponible';

  return `${date} ${humanTime}`;
}

function handleCancel() {
  show.value = false;
  emit('cancel');
}

function handleConfirm() {
  show.value = false;
  emit('confirm');
}
</script>

<template>
  <v-dialog
    v-model="show"
    persistent
    max-width="600"
  >
    <v-card>
      <v-card-title class="text-h5 bg-warning">
        {{ title }}
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert
          type="warning"
          variant="tonal"
        >
          Se {{ summaryVerb }}
          <strong>{{ summaryLabel }}</strong>
          {{ totalConflicts === 100 ? '(o más)' : '' }}
        </v-alert>

        <v-expansion-panels class="mt-4">
          <v-expansion-panel
            v-for="conflict in conflictsWithLimitedDetails"
            :key="conflict.caregiverId"
          >
            <v-expansion-panel-title>
              {{ conflict.caregiverName }}
              <span v-if="conflict.caregiverHumanCareer">
                ({{ conflict.caregiverHumanCareer }})
              </span>
              <v-chip
                v-if="conflictsWithLimitedDetails.length > 1"
                class="ml-2"
              >
                {{ conflict.conflictsCount }}
              </v-chip>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div
                v-for="(detail, idx) in (conflict.details || [])"
                :key="idx"
                class="mb-3"
              >
                <div v-if="detail.newShift">
                  <strong>Turno nuevo:</strong><br>
                  {{ formatShiftTime(detail.newShift.date, detail.newShift.humanTime) }}
                </div>

                <div
                  v-if="detail.existingShifts?.length"
                  class="ml-4 mt-2"
                >
                  <strong>Topa con:</strong>
                  <div
                    v-for="(existing, existingIndex) in detail.existingShifts"
                    :key="existing.id ?? `batch-${existingIndex}`"
                    class="ml-4 mt-1"
                  >
                    {{ formatShiftTime(existing.date, existing.humanTime) }}
                    <span
                      v-if="existing.id === null"
                      class="text-caption"
                    >(también en esta creación)</span><br>
                    <span class="text-caption">
                      Paciente: {{ existing.patientName }}<br>
                      Profesional: {{ existing.caregiverName }} ({{ existing.caregiverHumanCareer }})
                    </span>
                  </div>
                </div>

                <div
                  v-if="detail.scheduleBlocks?.length"
                  class="ml-4 mt-2"
                  data-testid="schedule-block-conflicts"
                >
                  <strong>Dentro de un bloqueo de horario del paciente:</strong>
                  <div
                    v-for="block in detail.scheduleBlocks"
                    :key="block.id"
                    class="ml-4 mt-1"
                  >
                    <v-icon
                      icon="mdi-calendar-lock"
                      size="x-small"
                      class="mr-1"
                    />{{ block.description }}<br>
                    <span class="text-caption">
                      {{ block.humanPeriod }}<br>
                      Especialidades: {{ block.humanCareers }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="detail.outsideRequirements?.length"
                  class="ml-4 mt-2"
                  data-testid="outside-requirement-conflicts"
                >
                  <strong>Fuera de las fechas del requerimiento del paciente:</strong>
                  <div
                    v-for="requirement in detail.outsideRequirements"
                    :key="requirement.id"
                    class="ml-4 mt-1"
                  >
                    <v-icon
                      icon="mdi-calendar-remove"
                      size="x-small"
                      class="mr-1"
                    />{{ requirement.humanCareer }}<br>
                    <span class="text-caption">
                      Vigente: {{ requirement.humanPeriod }}
                    </span>
                  </div>
                </div>
              </div>

              <v-alert
                v-if="conflict.hasMoreConflicts"
                type="info"
                variant="tonal"
                density="compact"
                class="mt-2"
              >
                ... y {{ conflict.hiddenConflictsCount }} tope(s) de horario más
              </v-alert>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert
          type="info"
          variant="tonal"
          class="mt-4"
        >
          ¿Estás seguro de que quieres {{ actionVerb }} estos turnos con {{ summaryNoun }}?
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="error"
          variant="text"
          @click="handleCancel"
        >
          Volver
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="handleConfirm"
        >
          Sí, {{ actionVerbCapitalized }} de Todos Modos
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
